import React, { useState, useEffect } from 'react';
import { useWebsiteData } from '../../../context/WebsiteDataContext';
import { uploadFile } from '../../../services/contentApi';

const TeamManager = () => {
  const { 
    teamMembers, 
    addTeamMember, 
    updateTeamMember, 
    deleteTeamMember,
    aboutSettings,
    updateAboutSettings 
  } = useWebsiteData();
  
  const [activeTab, setActiveTab] = useState('members');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  // Image Upload State
  const [isUploading, setIsUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  // About Page Settings State
  const [aboutIntro, setAboutIntro] = useState('');
  const [quoteText, setQuoteText] = useState('');
  const [quoteAuthor, setQuoteAuthor] = useState('');
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');

  // Sync settings when they load
  useEffect(() => {
    if (aboutSettings) {
      setAboutIntro(aboutSettings.aboutIntro || '');
      setQuoteText(aboutSettings.quoteText || '');
      setQuoteAuthor(aboutSettings.quoteAuthor || '');
    }
  }, [aboutSettings]);

  const handleOpenAdd = () => {
    setEditingMember(null);
    setImageUrl('');
    setShowAddModal(true);
  };

  const handleOpenEdit = (member) => {
    setEditingMember(member);
    setImageUrl(member.image || '');
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const res = await uploadFile(file);
      setImageUrl(res.url);
    } catch (err) {
      alert("Image upload failed: " + (err.message || err));
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const expertiseString = formData.get('expertise') || '';
    const expertiseArray = expertiseString.split(',').map(s => s.trim()).filter(Boolean);

    const data = {
      name: formData.get('name'),
      position: formData.get('position'),
      email: formData.get('email') || '',
      phone: formData.get('phone') || '',
      bio: formData.get('bio'),
      expertise: expertiseArray,
      image: imageUrl || editingMember?.image || '/assets/images/no_image.png',
      status: 'active'
    };

    try {
      if (editingMember) {
        await updateTeamMember(editingMember.id, data);
      } else {
        await addTeamMember(data);
      }
      setShowAddModal(false);
      setEditingMember(null);
      setImageUrl('');
    } catch (err) {
      alert("Error saving team member");
    }
  };

  const handleDelete = async (id) => {
    if (confirm('Are you sure you want to delete this team member?')) {
      try {
        await deleteTeamMember(id);
      } catch (err) {
        alert("Error deleting team member");
      }
    }
  };

  const handleSaveAboutSettings = async (e) => {
    e.preventDefault();
    setIsSavingSettings(true);
    setSaveStatus('');
    try {
      await updateAboutSettings({
        aboutIntro,
        quoteText,
        quoteAuthor
      });
      setSaveStatus('About Page settings updated successfully!');
      setTimeout(() => setSaveStatus(''), 4000);
    } catch (err) {
      console.error(err);
      setSaveStatus('Failed to update settings: ' + err.message);
    } finally {
      setIsSavingSettings(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">About Us & Team Management</h2>
          <p className="text-gray-600">Manage founders' profiles, about intro content, and joint quotes</p>
        </div>
        {activeTab === 'members' && (
          <button
            onClick={handleOpenAdd}
            className="bg-custom-brown text-white px-4 py-2 rounded-lg hover:bg-custom-brown/90 transition-colors duration-200 shadow-sm font-semibold"
          >
            Add Team Member
          </button>
        )}
      </div>

      {/* Tabs Menu */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('members')}
          className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors duration-200 ${
            activeTab === 'members'
              ? 'border-custom-brown text-custom-brown'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Founder Profiles / Cards
        </button>
        <button
          onClick={() => setActiveTab('about-settings')}
          className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors duration-200 ${
            activeTab === 'about-settings'
              ? 'border-custom-brown text-custom-brown'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Intro & Vision Quote
        </button>
      </div>

      {activeTab === 'members' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {teamMembers && teamMembers.map((member) => (
            <div key={member.id} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col justify-between hover:shadow-md transition-shadow duration-300">
              <div>
                <div className="aspect-w-16 aspect-h-9 bg-gray-100 relative h-48">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-5 space-y-3">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{member.name}</h3>
                    <p className="text-custom-brown text-sm font-medium">{member.position}</p>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-4">{member.bio}</p>
                  
                  {member.expertise && (
                    <div className="flex flex-wrap gap-1.5 pt-2">
                      {(Array.isArray(member.expertise)
                        ? member.expertise
                        : String(member.expertise).split(',').map(s => s.trim())
                      ).filter(Boolean).map((tag, idx) => (
                        <span key={idx} className="bg-custom-brown/5 text-custom-brown text-xs px-2 py-0.5 rounded border border-custom-brown/10">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="p-5 pt-0">
                <div className="text-xs text-gray-500 space-y-1 mb-4 border-t border-gray-100 pt-3">
                  {member.email && <p>✉️ {member.email}</p>}
                  {member.phone && <p>📞 {member.phone}</p>}
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => handleOpenEdit(member)}
                    className="flex-1 bg-custom-brown/10 text-custom-brown px-3 py-2 rounded-lg text-sm font-semibold hover:bg-custom-brown/20 transition-colors duration-200"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(member.id)}
                    className="flex-1 bg-red-50 text-red-600 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-red-100 transition-colors duration-200"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
          {(!teamMembers || teamMembers.length === 0) && (
            <div className="col-span-full py-12 text-center text-gray-500 bg-white border border-gray-200 rounded-lg shadow-sm">
              No team members found. Click "Add Team Member" to create one.
            </div>
          )}
        </div>
      )}

      {activeTab === 'about-settings' && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 max-w-4xl space-y-6 shadow-sm">
          <div>
            <h3 className="text-lg font-bold text-gray-900">About Us Page Introductory Context</h3>
            <p className="text-sm text-gray-500">Manage the introduction text block and the main Joint Vision quote displayed on the About Us page.</p>
          </div>

          {saveStatus && (
            <div className={`p-4 rounded-lg text-sm font-semibold border ${
              saveStatus.includes('successfully')
                ? 'bg-green-50 text-green-800 border-green-200'
                : 'bg-red-50 text-red-800 border-red-200'
            }`}>
              {saveStatus}
            </div>
          )}

          <form onSubmit={handleSaveAboutSettings} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                "Behind the Platform" Introduction Paragraph
              </label>
              <textarea
                rows="5"
                value={aboutIntro}
                onChange={(e) => setAboutIntro(e.target.value)}
                required
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                placeholder="Behind this platform is Nirmitee Associates..."
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Co-Founders Joint Vision Quote Text
              </label>
              <textarea
                rows="4"
                value={quoteText}
                onChange={(e) => setQuoteText(e.target.value)}
                required
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                placeholder="Together, the founders bring a balanced combination..."
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Quote Subtitle / Footer
              </label>
              <input
                type="text"
                value={quoteAuthor}
                onChange={(e) => setQuoteAuthor(e.target.value)}
                required
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                placeholder="Nirmitee Associates Co-Founders Vision"
              />
            </div>

            <div className="flex justify-end pt-4 border-t border-gray-100">
              <button
                type="submit"
                disabled={isSavingSettings}
                className="bg-custom-brown text-white px-5 py-2.5 rounded-lg hover:bg-custom-brown/90 transition-colors font-semibold shadow-sm disabled:opacity-50"
              >
                {isSavingSettings ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Add/Edit Team Member Modal */}
      {(showAddModal || editingMember) && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm animate-fade-in"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAddModal(false);
              setEditingMember(null);
            }
          }}
        >
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md flex flex-col max-h-[90vh] border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex-shrink-0 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">
                {editingMember ? 'Edit Team Member / Founder' : 'Add New Team Member / Founder'}
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingMember(null);
                }}
                className="text-gray-400 hover:text-gray-600 text-lg font-bold"
              >
                ✕
              </button>
            </div>

            <form className="flex flex-col min-h-0 flex-grow" onSubmit={handleSubmit}>
              <div className="p-6 space-y-4 overflow-y-auto flex-grow">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    defaultValue={editingMember?.name || ''}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Position / Designation
                  </label>
                  <input
                    type="text"
                    name="position"
                    required
                    defaultValue={editingMember?.position || ''}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                    placeholder="e.g. Co-Founder & Architect"
                  />
                </div>

                {/* Profile Photo Upload */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Profile Photo
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full overflow-hidden border border-gray-200 bg-gray-50 flex-shrink-0">
                      <img
                        src={imageUrl || editingMember?.image || '/assets/images/no_image.png'}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-grow">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="w-full text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-custom-brown/10 file:text-custom-brown hover:file:bg-custom-brown/20 cursor-pointer"
                      />
                      {isUploading && (
                        <p className="text-xs text-custom-brown mt-1 animate-pulse font-medium">
                          Uploading image to Cloudinary...
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Bio / Description
                  </label>
                  <textarea
                    rows="3"
                    name="bio"
                    required
                    defaultValue={editingMember?.bio || ''}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                    placeholder="Describe their expertise, experience, and role..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Expertise Fields (comma-separated list)
                  </label>
                  <input
                    type="text"
                    name="expertise"
                    defaultValue={
                      editingMember?.expertise
                        ? (Array.isArray(editingMember.expertise)
                            ? editingMember.expertise.join(', ')
                            : editingMember.expertise)
                        : ''
                    }
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                    placeholder="e.g. Architectural Design, Space Planning, Liaisoning"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      defaultValue={editingMember?.email || ''}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      defaultValue={editingMember?.phone || ''}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                    />
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-gray-200 bg-gray-50 flex gap-3 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingMember(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors font-medium text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUploading}
                  className="flex-1 bg-custom-brown text-white px-4 py-2 rounded-md hover:bg-custom-brown/90 transition-colors flex items-center justify-center font-medium text-sm shadow-sm disabled:opacity-50"
                >
                  {editingMember ? 'Update' : 'Add'} Member
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManager;